import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useQueryClient } from "react-query";

import useUser from "../../../../hooks/useUser";
import useQuery from "../../../../hooks/useQuery";
import File from "./File";

const sort = (a, b) => {
  if (!a.id && b.id) return -1;
  if (a.id && !b.id) return 1;
  return b.createdAt - a.createdAt;
};

function Files({ consumer, uid, className, resource }) {
  const { t } = useTranslation();
  const $container = useRef(null);
  const queryClient = useQueryClient();
  const $files = useRef(null);
  // Bug real (auditoría 2026-08-14): `files` guardaba los `File` crudos en
  // un array plano ordenado por alta, y el render los matcheaba contra
  // `records` (que se re-ordena por `sort`, más nuevo primero) por
  // POSICIÓN de índice — con 2+ archivos pendientes el orden diverge y el
  // binario equivocado terminaba subiéndose contra el registro equivocado.
  // Además `fileData` era un `let` de cuerpo de componente (se
  // reinicializa a `[]` en cada render, no persiste como useState/useRef):
  // una segunda selección de archivos en una acción separada pisaba
  // `files` entero con sólo los nuevos, perdiendo el tracking de los
  // agregados antes. Se guarda ahora un mapa `{ [createdAt]: File }`
  // keyeado por el mismo `createdAt` que identifica al record (la misma
  // identidad que ya usan `key=` y `update()` más abajo), actualizado con
  // setState funcional para que altas separadas se acumulen en vez de
  // pisarse, y el render matchea por esa clave en vez de por índice.
  const [files, setFiles] = useState({});

  const [status, setStatus] = useState([
    "unclassified",
    "tc",
    "rmn",
    "rx",
    "lab",
    "report",
  ]);
  const user = useUser();

  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});

  // Bug real (auditoría 2026-08-14, mismo patrón ya corregido en Labs.js):
  // `update()` ubicaba las queries de cache con `queryHash.includes("/file")`
  // — un substring que matchea la de CUALQUIER paciente cacheado
  // (react-query no evict la cache de un paciente sólo porque su
  // componente se desmontó al navegar a otro). Subir un archivo del
  // paciente ACTUAL terminaba agregándolo también al array cacheado de
  // otro paciente ya visitado en la sesión.
  const endpoint = window._l(`/${consumer.id}/file`).substr(1);
  const query = useQuery(endpoint, {
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const licenses =
    resource &&
    resource.licenses &&
    resource.licenses
      .filter((element) => element.type === "license")
      .map((license) => {
        if (license.jurisdiction === "AR") {
          return `M.N. ${license.value}`;
        } else {
          return `M.P. ${license.value}`;
        }
      })
      .join("-");

  const add = (name, file) => {
    const date = new Date();
    const today = `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    const createdAt = Date.now();
    update({
      type: "file",
      consumer: { id: consumer.id },
      createdBy: user.state.uid,
      createdByName: resource.label,
      createdByLicense: licenses,
      createdAt,
      status: "",
      startsAt: today,
      content: "",
      filename: name,
    });

    setFiles((previous) => ({ ...previous, [createdAt]: file }));
  };

  const update = (payload, clear) => {
    const id = payload.id || payload.createdAt;
    const keys = queryClient
      .getQueryCache()
      .findAll()
      .filter((query) => query.queryHash.includes(endpoint))
      .map((query) => query.queryKey);

    for (const key of keys) {
      queryClient.setQueryData(key, (records) => {
        const array = JSON.clone(records);
        const index = array.findIndex(
          (record) =>
            (record.id || record.createdAt) === id &&
            record.consumer?.id === payload.consumer?.id
        );

        if (index < 0) {
          return [...array, payload].filter((record) => !clear || record.id);
        }

        array[index] = payload;
        return array;
      });
    }
  };

  const handleFiles = (e, record, setFile, setRecord) => {
    const files = Array.from(e.target.files);

    if (!record) {
      files.forEach((file) => add(file.name, file));
    } else {
      setFile(files[0]);
      record.filename = files[0].name;
      setRecord({ ...record });
    }
  };

  const toggle = (value) => {
    if (status.includes(value)) {
      setStatus(status.filter((s) => s !== value));
    } else {
      setStatus([...status, value]);
    }
  };

  useEffect(() => {
    const array = [];
    const stats = {};

    for (const record of query.data) {
      if (uid && record.createdBy !== uid) {
        continue;
      }

      if (record.deleted) {
        stats.deleted = stats.deleted || 0;
        stats.deleted += 1;
      }

      if (record.deleted && !status.includes("deleted")) {
        continue;
      }

      if (!record.status) {
        record.status = "unclassified";
      }

      if (record.status && !record.deleted) {
        stats[record.status] = stats[record.status] || 0;
        stats[record.status] += 1;
      }
      if (
        !record.id ||
        status.includes(record.status) ||
        status.includes("deleted")
      ) {
        array.push(record);
      }
    }

    setRecords(array);
    setStats(stats);
  }, [query.data, status, uid]);

  const filters = Object.keys(stats || {}).sort((a, b) => {
    if (a === "deleted" && b !== "deleted") return 1;
    if (a !== "deleted" && b === "deleted") return -1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });

  return (
    <div className={className || ""}>
      {/* Bug real (auditoría 2026-08-21): sin flex-wrap, con 3+ estados
          activos los pills de filtro se apretaban contra "Nuevo" o
          quedaban fuera de pantalla en mobile. */}
      <div className="d-flex align-items-center flex-wrap">
        <button
          className="btn btn-sm btn link-success ms-n2"
          onClick={() => {
            $files.current.click();
          }}
        >
          {t("new", { label: t("file") })}
        </button>
        <input
          ref={$files}
          type="file"
          className="form-control d-none"
          id="files"
          name="files[]"
          onChange={(e) => {
            handleFiles(e);
          }}
        />

        {filters.length > 0 && (
          <>
            <div className="flex-fill"></div>
            {filters.map((key) => (
              <button
                key={`status-${key}`}
                onClick={() => toggle(key)}
                className={cx("btn btn-sm btn-link", {
                  "link-primary": status.includes(key),
                  "link-secondary": !status.includes(key),
                })}
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t(`${key}_tooltip`)}
              >
                {t(`ehr:${key}`, { count: stats[key] })}
                <sup>{stats[key]}</sup>
              </button>
            ))}
          </>
        )}
      </div>
      <div ref={$container}>
        {records.sort(sort).map((record) => (
          <File
            key={record.id || record.createdAt}
            record={record}
            update={update}
            handleFiles={handleFiles}
            file={files[record.id || record.createdAt]}
          />
        ))}
      </div>
    </div>
  );
}

export default Files;
